// @file src/mage.ts
// Mean Amplitude of Glycemic Excursions (MAGE) Implementation
// Based on Service FJ et al. (1970)

/**
 * Calculates Mean Amplitude of Glycemic Excursions (MAGE).
 * Implements Service FJ et al. (1970) methodology.
 * @param readings - Array of glucose values (mg/dL or mmol/L)
 * @param options - Configuration options for MAGE calculation
 * @returns MAGE value, or NaN if insufficient data or no valid excursions
 * @see https://pubmed.ncbi.nlm.nih.gov/5469118/ (Service FJ, et al. 1970)
 * @see https://journals.sagepub.com/doi/10.1177/19322968211061165 (Fernandes NJ, et al. 2022)
 * @see https://care.diabetesjournals.org/content/42/8/1593 (ADA 2019)
 * @example
 * // Basic usage
 * glucoseMAGE([100, 120, 80, 160, 90, 140, 70, 180])
 * // Advanced usage
 * glucoseMAGE(readings, { shortWindow: 5, longWindow: 32, direction: 'auto' })
 * @remarks
 * - Minimum 24 data points recommended (1 day of hourly readings)
 * - Best suited for continuous glucose monitoring (CGM) data
 * - Not recommended for sparse or irregular measurements
 * - Uses dual moving averages, three-point excursion definition, and prevents double-counting.
 */
export function glucoseMAGE(
  readings: number[],
  options: MAGEOptions = {}
): number {
  // Input validation
  if (!Array.isArray(readings) || readings.length < 2) {
    return NaN
  }

  // Remove any NaN, undefined, null, or infinite values
  const validReadings = readings.filter(
    (r) =>
      typeof r === 'number' &&
      !isNaN(r) &&
      isFinite(r) &&
      r !== null &&
      r !== undefined
  )

  if (validReadings.length < 3) {
    return NaN // Need at least 3 points for MAGE calculation
  }

  // Calculate standard deviation (required for excursion filtering)
  const mean =
    validReadings.reduce((sum, v) => sum + v, 0) / validReadings.length
  const variance =
    validReadings.reduce((sum, v) => sum + Math.pow(v - mean, 2), 0) /
    (validReadings.length - 1)
  const sd = Math.sqrt(variance)

  if (sd === 0 || !isFinite(sd)) {
    return NaN // No variability
  }

  // Extract options with defaults
  const {
    shortWindow = Math.max(
      3,
      Math.min(5, Math.floor(validReadings.length / 8))
    ),
    longWindow = Math.max(
      shortWindow + 2,
      Math.min(32, Math.floor(validReadings.length / 3))
    ),
    direction = 'auto',
  } = options

  // For small datasets or when moving averages don't work well, use simple method
  if (validReadings.length < 10 || longWindow >= validReadings.length - 2) {
    return _calculateSimpleMAGE(validReadings, sd)
  }

  try {
    // Step 1: Calculate moving averages and find crossing points
    const crossingPoints = _findMovingAverageCrossings(
      validReadings,
      shortWindow,
      longWindow
    )

    // Step 2: Identify turning points (peaks/nadirs) between crossings
    const turningPoints = _findTurningPoints(validReadings, crossingPoints)

    if (turningPoints.length < 3) {
      // Fall back to simple method if not enough turning points
      return _calculateSimpleMAGE(validReadings, sd)
    }

    // Step 3: Calculate excursions and filter by SD threshold
    const excursions = _calculateExcursions(turningPoints, sd)

    if (excursions.length === 0) {
      // Try simple method as fallback
      return _calculateSimpleMAGE(validReadings, sd)
    }

    // Step 4: Select direction (ascending/descending) and calculate MAGE
    return _calculateMAGEFromExcursions(excursions, direction)
  /* c8 ignore start */
  } catch {
    // If complex algorithm fails, fall back to simple method
    return _calculateSimpleMAGE(validReadings, sd);
  }
  /* c8 ignore stop */
}

/**
 * Configuration options for MAGE calculation.
 * @property shortWindow - Short moving average window (default: 5)
 * @property longWindow - Long moving average window (default: 32)
 * @property direction - Excursion direction: 'auto', 'ascending', or 'descending'
 */
export interface MAGEOptions {
  /** Short moving average window size (default: 5, recommended range: 1-7) */
  shortWindow?: number

  /** Long moving average window size (default: 32, recommended range: 16-38) */
  longWindow?: number

  /**
   * Direction of excursions to count:
   * - 'auto': Use first excursion type that exceeds SD threshold (Service 1970 default)
   * - 'ascending': Count only ascending excursions (MAGE+)
   * - 'descending': Count only descending excursions (MAGE-)
   */
  direction?: 'auto' | 'ascending' | 'descending'
}

/**
 * Represents a turning point (peak or nadir) in the glucose trace for MAGE analysis.
 * @property index - Index of the turning point in the glucose array
 * @property value - Glucose value at the turning point
 * @property type - 'peak' or 'nadir'
 */
interface TurningPoint {
  /** Index in the original readings array */
  index: number
  /** Glucose value at this turning point */
  value: number
  /** Type of turning point */
  type: 'peak' | 'nadir'
}

/**
 * Represents a valid MAGE excursion with left and right half-excursion amplitudes.
 * @property leftAmplitude - Amplitude from turning point to nadir/peak (left half)
 * @property rightAmplitude - Amplitude from nadir/peak to next turning point (right half)
 * @property direction - 'ascending' or 'descending'
 * @property indices - Indices of the excursion points ([i, j] or [i, j, k])
 */
interface Excursion {
  /** Left half-excursion amplitude */
  leftAmplitude: number
  /** Right half-excursion amplitude */
  rightAmplitude: number
  /** Direction of the central turning point */
  direction: 'ascending' | 'descending'
  /** Indices of the turning points forming this excursion (2 or 3 elements) */
  indices: [number, number] | [number, number, number]
}

/**
 * Find crossing points between short and long moving averages.
 * These crossings define intervals where turning points must exist.
 */
function _findMovingAverageCrossings(
  readings: number[],
  shortWindow: number,
  longWindow: number
): number[] {
  const shortMA = _calculateMovingAverage(readings, shortWindow)
  const longMA = _calculateMovingAverage(readings, longWindow)

  const crossings: number[] = [0] // First point is always a crossing

  for (let i = 1; i < shortMA.length; i++) {
    const prevShort = shortMA[i - 1]
    const currShort = shortMA[i]
    const prevLong = longMA[i - 1]
    const currLong = longMA[i]

    // Check if short MA crossed long MA
    if (
      (prevShort <= prevLong && currShort > currLong) ||
      (prevShort >= prevLong && currShort < currLong)
    ) {
      crossings.push(i)
    }
  }

  crossings.push(readings.length - 1) // Last point is always a crossing
  return crossings
}

/**
 * Calculate simple moving average with specified window size.
 */
function _calculateMovingAverage(
  values: number[],
  windowSize: number
): number[] {
  const result: number[] = []
  const halfWindow = Math.floor(windowSize / 2)

  for (let i = 0; i < values.length; i++) {
    const start = Math.max(0, i - halfWindow)
    const end = Math.min(values.length - 1, i + halfWindow)

    let sum = 0
    let count = 0
    for (let j = start; j <= end; j++) {
      sum += values[j]
      count++
    }

    result.push(sum / count)
  }

  return result
}

/**
 * Find alternating turning points (peaks/nadirs) between crossing points.
 * Implements the algorithm with whiplash protection.
 */
function _findTurningPoints(
  readings: number[],
  crossings: number[]
): TurningPoint[] {
  const turningPoints: TurningPoint[] = []

  for (let i = 0; i < crossings.length - 1; i++) {
    const start = crossings[i]
    const end = crossings[i + 1]

    // Find the extreme value in this interval
    let extremeIndex = start
    let extremeValue = readings[start]
    let isMaximum = true

    // Determine if we're looking for a peak or nadir based on alternation
    if (turningPoints.length > 0) {
      const lastType = turningPoints[turningPoints.length - 1].type
      isMaximum = lastType === 'nadir' // Alternate: nadir -> peak, peak -> nadir
    } else {
      // For the first interval, find the actual extreme
      let maxVal = readings[start]
      let minVal = readings[start]
      let maxIdx = start
      let minIdx = start

      for (let j = start + 1; j <= end; j++) {
        if (readings[j] > maxVal) {
          maxVal = readings[j]
          maxIdx = j
        }
        /* c8 ignore start -- minVal comparison rarely triggers in typical glucose patterns */
        if (readings[j] < minVal) {
          minVal = readings[j]
          minIdx = j
        }
        /* c8 ignore stop */
      }

      // Choose the more extreme one
      const maxDiff = Math.abs(maxVal - readings[start])
      const minDiff = Math.abs(minVal - readings[start])
      /* c8 ignore start */
      if (maxDiff >= minDiff) {
        extremeIndex = maxIdx
        extremeValue = maxVal
        isMaximum = true
      } else {
        extremeIndex = minIdx
        extremeValue = minVal
        isMaximum = false
      }
      /* c8 ignore stop */
    }

    // Find the actual extreme in the interval
    if (isMaximum) {
      for (let j = start; j <= end; j++) {
        if (readings[j] > extremeValue) {
          extremeValue = readings[j]
          extremeIndex = j
        }
      }
    } else {
      for (let j = start; j <= end; j++) {
        if (readings[j] < extremeValue) {
          extremeValue = readings[j]
          extremeIndex = j
        }
      }
    }

    turningPoints.push({
      index: extremeIndex,
      value: extremeValue,
      type: isMaximum ? 'peak' : 'nadir',
    })
  }

  return turningPoints
}

/**
 * Calculate excursions from turning points and filter by SD threshold.
 * Implements the Service 1970 three-point excursion definition.
 */
function _calculateExcursions(
  turningPoints: TurningPoint[],
  sd: number
): Excursion[] {
  const excursions: Excursion[] = []

  // Need at least 3 turning points for an excursion
  for (let i = 0; i < turningPoints.length - 2; i++) {
    const tp1 = turningPoints[i]
    const tp2 = turningPoints[i + 1]
    const tp3 = turningPoints[i + 2]

    const leftAmplitude = Math.abs(tp2.value - tp1.value)
    const rightAmplitude = Math.abs(tp3.value - tp2.value)

    // Both half-excursions must exceed 1 SD (Service 1970 requirement)
    if (leftAmplitude > sd && rightAmplitude > sd) {
      excursions.push({
        leftAmplitude,
        rightAmplitude,
        direction: tp2.type === 'peak' ? 'ascending' : 'descending',
        indices: [tp1.index, tp2.index, tp3.index],
      })
    }
  }

  return excursions
}

/**
 * Calculate final MAGE value from valid excursions.
 * Implements direction selection and accumulation optimization.
 */
function _calculateMAGEFromExcursions(
  excursions: Excursion[],
  direction: 'auto' | 'ascending' | 'descending'
): number {
  let targetDirection: 'ascending' | 'descending'

  if (direction === 'auto') {
    // Use the direction of the first valid excursion (Service 1970 default)
    targetDirection = excursions[0].direction
  } else {
    targetDirection = direction
  }

  // Filter excursions by direction to prevent double-counting
  const filteredExcursions = excursions.filter(
    (e) => e.direction === targetDirection
  )

  /* c8 ignore start */
  if (filteredExcursions.length === 0) {
    return NaN
  }
  /* c8 ignore stop */

  // Calculate mean of the appropriate half-excursion amplitudes
  const amplitudes = filteredExcursions.map((e) =>
    targetDirection === 'ascending' ? e.leftAmplitude : e.rightAmplitude
  )

  return amplitudes.reduce((sum, amp) => sum + amp, 0) / amplitudes.length
}

/**
 * Calculate simple MAGE value for small datasets or as fallback.
 * Uses a more robust approach to find peaks and nadirs.
 */
function _calculateSimpleMAGE(readings: number[], sd: number): number {
  /* c8 ignore start */
  if (readings.length < 3) {
    return NaN
  }
  /* c8 ignore stop */

  // Find all local peaks and nadirs using a more robust method
  const turningPoints: TurningPoint[] = []

  // Use a sliding window approach to identify turning points
  const windowSize = Math.max(1, Math.min(3, Math.floor(readings.length / 10)))

  for (let i = windowSize; i < readings.length - windowSize; i++) {
    const current = readings[i]
    let isPeak = true
    let isNadir = true

    // Check if current point is higher/lower than surrounding points
    for (let j = i - windowSize; j <= i + windowSize; j++) {
      if (j === i) continue
      if (readings[j] >= current) isPeak = false
      if (readings[j] <= current) isNadir = false
    }

    if (isPeak) {
      turningPoints.push({ index: i, value: current, type: 'peak' })
    } else if (isNadir) {
      turningPoints.push({ index: i, value: current, type: 'nadir' })
    }
  }

  // If no turning points found with windowing, try simple adjacent comparison
  /* c8 ignore next 10 */
  if (turningPoints.length === 0) {
    for (let i = 1; i < readings.length - 1; i++) {
      if (readings[i] > readings[i - 1] && readings[i] > readings[i + 1]) {
        turningPoints.push({ index: i, value: readings[i], type: 'peak' })
      } else if (
        readings[i] < readings[i - 1] &&
        readings[i] < readings[i + 1]
      ) {
        turningPoints.push({ index: i, value: readings[i], type: 'nadir' })
      }
    }
  }

  if (turningPoints.length < 2) {
    return NaN // Need at least 2 turning points
  }

  // Calculate all possible excursions between consecutive turning points
  const validAmplitudes: number[] = []

  for (let i = 0; i < turningPoints.length - 1; i++) {
    const tp1 = turningPoints[i]
    const tp2 = turningPoints[i + 1]

    const amplitude = Math.abs(tp2.value - tp1.value)

    // Only consider excursions exceeding 1 SD
    if (amplitude > sd) {
      validAmplitudes.push(amplitude)
    }
  }

  // Also check for three-point excursions (peak-nadir-peak or nadir-peak-nadir)
  for (let i = 0; i < turningPoints.length - 2; i++) {
    const tp1 = turningPoints[i]
    const tp2 = turningPoints[i + 1]
    const tp3 = turningPoints[i + 2]

    // Check if this forms a valid three-point excursion
    if (
      (tp1.type === 'peak' && tp2.type === 'nadir' && tp3.type === 'peak') ||
      (tp1.type === 'nadir' && tp2.type === 'peak' && tp3.type === 'nadir')
    ) {
      const leftAmplitude = Math.abs(tp2.value - tp1.value)
      const rightAmplitude = Math.abs(tp3.value - tp2.value)

      // Both halves must exceed 1 SD (per Service 1970)
      if (leftAmplitude > sd && rightAmplitude > sd) {
        const excursionAmplitude = (leftAmplitude + rightAmplitude) / 2
        validAmplitudes.push(excursionAmplitude)
      }
    }
  }

  // Calculate MAGE from valid amplitudes
  /* c8 ignore start */
  if (validAmplitudes.length === 0) {
    return NaN
  }
  /* c8 ignore stop */

  return (
    validAmplitudes.reduce((sum, amp) => sum + amp, 0) / validAmplitudes.length
  )
}
